import type { ResearchCard } from "./researchCard";

/**
 * What the check reads: the VIP signal codes, the inference codes and the
 * enrichment outcome. A full `ResearchCard` satisfies it, and so does the
 * reply's loaded `LeadCardForReply` (U4), which is how the check runs in
 * production: the compose path passes the card it loaded for the [LEAD] lines.
 */
export interface CardRevealInput {
  readonly vip: { readonly signals: readonly string[] };
  readonly inferences: readonly { readonly code: string }[];
  readonly enrichmentOutcome: string;
}
const _full: CardRevealInput = null as unknown as ResearchCard; // compile-time: ResearchCard satisfies the input
void _full;

/**
 * U1 RESEARCH, the never-reveal rule as a verifier check (Agent Brains 5.1 L4,
 * Master Spec B2: VIP and profile signals are "never stated to the customer";
 * B14). The compose prompt already carries the rule as a constraint line and
 * `aiTell.ts` has a surveillance tell. This is the backstop that holds a
 * research-sourced word when it reaches a draft by any route.
 *
 * It reads only research-sourced signals PRESENT ON THE CARD:
 *  - follower words, when the card carries a follower-bucket signal;
 *  - "verified account" and "blue check", when it carries `vip.ig_verified`
 *    (never bare "verified": that is a certificate word in jewelry);
 *  - press phrasing near "you", when it carries `vip.press_mention`;
 *  - a tone label phrased about the person ("you seem formal"), when the card
 *    has that label as an inference;
 *  - "your profile", "your bio", "your account", when any enrichment signal is
 *    on the card.
 * Customer-stated memory is excluded: quoting a customer's own size back is
 * service, not surveillance.
 *
 * A hit adds `card_reveal`, which takes the verifier's existing one corrective
 * retry and then the block and escalation. A false hit blocks, the safe
 * direction (CLAUDE.md section 0). AWAITING RATIFICATION: the closed phrase lists.
 */

export const CARD_REVEAL_VIOLATION = "card_reveal" as const;

export interface CardRevealCheck {
  readonly violations: readonly "card_reveal"[];
  /** Which patterns fired, one short line each, for the operator. Never the reply text. */
  readonly checks: readonly string[];
}

const FOLLOWER_SIGNALS = new Set(["vip.followers_10k_100k", "vip.followers_100k_1m", "vip.followers_gte_1m"]);

const FOLLOWERS = /\bfollowers?\b/i;
const VERIFIED_PROFILE = /\bverified\s+(?:account|profile|badge)\b|\bblue\s+(?:check|tick)(?:mark)?\b/i;
/** press / article / featured / interview within 30 characters of "you". */
const PRESS_NEAR_YOU =
  /\b(?:press|article|featured|interview)\b[^.!?\n]{0,30}\byou(?:r|'re|'ve)?\b|\byou(?:r|'re|'ve)?\b[^.!?\n]{0,30}\b(?:press|article|featured|interview)\b/i;
const PROFILE_WORDS = /\byour\s+(?:profile|bio|account|page|feed)\b/i;

function toneWord(code: string): string | null {
  return code.startsWith("tone.") && code !== "tone.unknown" ? code.slice("tone.".length) : null;
}

function personPhrase(word: string): RegExp {
  return new RegExp(
    `\\byou(?:'re|\\s+are|\\s+seem|\\s+sound|\\s+come\\s+across(?:\\s+as)?)\\s+(?:very\\s+|quite\\s+|rather\\s+|a\\s+bit\\s+|so\\s+)?${word}\\b`,
    "i",
  );
}

export function checkCardReveal(reply: string, card: CardRevealInput | null): CardRevealCheck {
  if (card === null) return { violations: [], checks: [] };
  const text = reply.replace(/[‘’]/g, "'");
  const vip = new Set<string>(card.vip.signals);
  const checks: string[] = [];

  if ([...vip].some((s) => FOLLOWER_SIGNALS.has(s)) && FOLLOWERS.test(text)) {
    checks.push("follower mention with a follower signal on the card");
  }
  if (vip.has("vip.ig_verified") && VERIFIED_PROFILE.test(text)) {
    checks.push("verified-profile phrase with a verified signal on the card");
  }
  if (vip.has("vip.press_mention") && PRESS_NEAR_YOU.test(text)) {
    checks.push("press phrasing near you with a press signal on the card");
  }
  for (const inf of card.inferences) {
    const w = toneWord(inf.code);
    if (w !== null && personPhrase(w).test(text)) checks.push(`tone label phrased about the person (${inf.code})`);
  }
  if ((card.enrichmentOutcome === "signals" || vip.size > 0) && PROFILE_WORDS.test(text)) {
    checks.push("profile or bio reference with an enrichment signal on the card");
  }

  return checks.length === 0 ? { violations: [], checks: [] } : { violations: ["card_reveal"], checks };
}

interface VerdictLike {
  readonly ok: boolean;
  readonly violations: readonly string[];
  readonly detail: readonly string[];
}

/**
 * The verifier's verdict with the reveal check folded in, the shape of
 * `withDisclosure`: a clean check returns the verdict object itself, so a
 * draft with no card (or no hit) sees no difference anywhere.
 */
export function withCardReveal<V extends VerdictLike>(v: V, r: CardRevealCheck): V {
  if (r.violations.length === 0) return v;
  return {
    ...v,
    ok: false,
    violations: [...v.violations, ...r.violations],
    detail: [
      ...v.detail,
      `the reply states something drawn from research on the customer (${r.checks.join("; ")}); a research signal is never said to the customer (Agent Brains 5.1, B2)`,
    ],
  } as V;
}
